import { CheckboxField } from '@e3dc-react/shell/Elements/InputFields'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Box, Button, Link, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'
import { makeStyles } from 'tss-react/mui'
import { createLuoxEnergyCloudKey } from '../../../apis/installation-extensions.api'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useTypedSelector } from 'src/State/RootReducer'
import { DATA_SECURITY_LINK } from 'src/Configuration'
import { CloudKeyModel } from 'src/Libs/installation-rights-management/models'
import { useIsWebviewMode } from 'src/Hooks'

interface StyleProps {
  isOptIn: boolean
}

const useStyles = makeStyles<StyleProps>()((theme, props) => ({
  button: {
    borderRadius: theme.spacing(12),
    textDecoration: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  link: {
    color: theme.palette.primary.dark,
    textDecoration: 'underline',
  },
  revenueCalculatorLink: {
    textDecoration: 'none',
    '&::after': {
      textDecoration: 'none',
    },
  },
}))

interface Props {
  luoxEnergyCloudKey?: CloudKeyModel
  className?: string
}

const REDIRECT_URL_BASE = 'https://erloesrechner.luox-energy.de'
/** Luox Energy and e3dc have a partnership. The affiliation can be used to redirect to Luox Energy from e3dc */
const AFFILIATION = 'spid=edreidc&utm_source=partner&utm_medium=referral&utm_content=affiliate-link&utm_campaign=edreidc'
/** a cloud key is send via this parameter which can be used by Luox Energy to access the installation */
const CLOUD_KEY_PARAMETER = 'authentication_id'

/**
 * This button directs to the external direct marketing (Luox Energy) page
 */
const RevenueCalculatorButton: React.VFC<Props> = ({ luoxEnergyCloudKey, className }) => {
  const { t } = useCustomTranslation()
  const [isOptIn, setIsOptIn] = useState<boolean>(false)
  const [redirectUrl, setRedirectUrl] = useState<string>()
  const { classes } = useStyles({ isOptIn })
  const isWebview = useIsWebviewMode()

  const changeOptIn = useCallback(
    (value: boolean | ((prevState: boolean) => boolean)) => {
      setIsOptIn(value)
      const mustCreateCloudKey = value && !redirectUrl && !luoxEnergyCloudKey
      if (mustCreateCloudKey) handleClickLink()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [redirectUrl, luoxEnergyCloudKey]
  )

  const activeInstallation = useTypedSelector(state => state.storage.activeInstallation)

  const authFetch = useAuthFetchWithResponse()

  const handleClickLink = (): void => {
    const redirectUrlWithoutKey = `${REDIRECT_URL_BASE}?${AFFILIATION}&${CLOUD_KEY_PARAMETER}=`

    if (luoxEnergyCloudKey) {
      setRedirectUrl(`${redirectUrlWithoutKey}${luoxEnergyCloudKey.key}`)
    }

    if (!luoxEnergyCloudKey) {
      createLuoxEnergyCloudKey(Number(activeInstallation), authFetch)
        .then(res => {
          if (res.value !== null) {
            const redirectUrl = `${redirectUrlWithoutKey}${res.value}`
            setRedirectUrl(redirectUrl)
          }
        })
        .catch((err: Error) => {
          console.error(`error creating luox energy key, ${err}`)
        })
    }
  }

  return (
    <>
      <Typography variant="body2">
        {t('installation-extensions.direct-marketing.privacy-protection')}
        <Link href={DATA_SECURITY_LINK} target={isWebview ? '_blank' : '_self'} rel="noreferrer" className={classes.link}>
          {t('installation-extensions.direct-marketing.privacy-protection-page')}
        </Link>
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        <CheckboxField
          sx={{ color: 'grey.grey05', ml: { xs: 1, md: 0 } }}
          onChange={changeOptIn}
          value={isOptIn}
          label={t('installation-extensions.direct-marketing.Modal.opt-in-text')}
        />
        <Button className={className ?? classes.button} onClick={handleClickLink} disabled={!isOptIn}>
          <a href={redirectUrl} target={'_blank'} rel={'noreferrer'} className={classes.revenueCalculatorLink}>
            {t('installation-extensions.direct-marketing.revenue-calculator-button')}
          </a>
        </Button>
      </Box>
    </>
  )
}

export default RevenueCalculatorButton
